package nl.volleyplanner.web.component.backend_jobs

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.backend_jobs.*
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.BootstrapButton
import nl.volleyplanner.web.base.component.ButtonVariant
import nl.volleyplanner.web.base.component.ModalComponent
import org.w3c.dom.HTMLElement


class JobFlowComponent(
    private val jobType: JobType,
    private val jobArguments: List<String>,
    private val buttonLabel: String,
    private val jobStartText: String? = null,
    private val enabled: Boolean = true,
    private val onJobLaunched: ((BackendJobTracker) -> Unit)? = null
) : HTMLComponent() {
    private val logger = Logger.getLogger("nl.volleyplanner.web.components.JobFlowComponent")

    private val jobTrackerFlow = MutableStateFlow<BackendJobTracker?>(null)

    init {
        CoroutineManager.RENDER_SCOPE.launch {
            val tracker = BackendJobManager.getAndMonitorLastJob(jobType)
            jobTrackerFlow.value = tracker
        }
    }

    inner class JobStartModal() : ModalComponent(
        buttonLabel
    ) {
        override val footerButtons = listOf(
                BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
                BootstrapButton("Start", ButtonVariant.Primary, { launchJob() }, mapOf("data-bs-dismiss" to "modal"))
            )

        override fun FlowContent.renderBody() {
            p {
                if (jobStartText == null) {
                    +"Start import job"
                } else {
                    unsafe { +jobStartText }
                }
            }
        }
    }

    fun launchJob() {
        launchAsync {
            BackendJobManager.launchJob(jobType, jobArguments)
        }.onComplete { jobTracker ->
            jobTrackerFlow.value = jobTracker
            onJobLaunched?.invoke(jobTracker)
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        // Default state: button to launch flow
        // Button click: show model on actions that are going to happen. Potentially input parameters
        // Cancel -> do nothing, back to default state
        // OK -> start flow, button shows spinner and state
        //  Click button during flow: show modal with details
        // Job finish: button shows state (color for success/fail)
        // Click button after finish: show modal with details. Possibility to restart
        return consumer.button (classes = "btn me-2") {
            val currentState = jobTrackerFlow.value?.stateFlow?.value
            classes += when {
                !enabled -> "btn-secondary"
                currentState?.isCompleted() == true -> {
                    if (currentState.jobExecution!!.result == JobResult.Failure) {
                        "btn-danger"
                    } else {
                        "btn-success"
                    }
                }

                currentState?.isScheduled() == true -> "btn-secondary"
                else -> "btn-primary"
            }
            type = ButtonType.button
            disabled = !enabled

            val jobStartModal = JobStartModal()
            onClickFunction = { event ->
                event.preventDefault()
                if (enabled) {
                    CoroutineScope(Dispatchers.Main).launch {
                        val eventState = jobTrackerFlow.value?.stateFlow?.value
                        if (eventState == null || eventState.isCompleted()) {
                            createStandaloneComponent(jobStartModal)
                                .show()
                        } else {
                            val jobProgressModal = JobProgressModal(jobTrackerFlow.value!!.stateFlow, buttonLabel)
                            createStandaloneComponent(jobProgressModal)
                                .show()
                        }
                    }
                }
            }

            if (currentState?.isScheduled() == true && !currentState.isCompleted()) {
                span (classes="spinner-border spinner-border-sm") {
                    role = "status"
                    attributes["aria-hidden"] = "true"
                }
            }

            +StringBuilder(buttonLabel)
                .apply {
                    if (currentState?.isScheduled() == true && !currentState.isCompleted()) {
                        append(" [${currentState.pollIndex}]")
                    }
                }
                .toString()
        }
    }

    override suspend fun getState() = jobTrackerFlow
}